import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Shield, FileText, Briefcase, Edit, Trash2, Loader2, Sparkles, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useCandidate } from '../../hooks/useCandidates';
import SocialLinksManager from './SocialLinksManager';
import AddCandidateModal from './AddCandidateModal';
import DetailContainer from '../ui/detail-container';
import CandidatePublicDisplay from './CandidatePublicDisplay';
import { supabase } from '../../lib/supabaseClient';

const FILE_TYPE_LABELS = {
  cv: 'Resume',
  cover_letter: 'Cover Letter',
  portfolio: 'Portfolio',
  certification: 'Certification',
  other: 'Other',
};

const CandidateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { candidate, skills, files, applications, loading, error, refetch } = useCandidate(id);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [generatingProfile, setGeneratingProfile] = useState(null); // 'full' | 'redacted' | null

  const handleGenerateProfile = async (redacted) => {
    try {
      setGeneratingProfile(redacted ? 'redacted' : 'full');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
      const response = await fetch(`${API_URL}/candidate-profile/${id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ redacted })
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Failed to generate profile');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = redacted
        ? `indicative-profile-deidentified-${id.slice(0, 8)}.pdf`
        : `indicative-profile-${candidate.first_name}-${candidate.last_name}.pdf`.toLowerCase();
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating profile:', err);
      alert('Failed to generate profile: ' + err.message);
    } finally {
      setGeneratingProfile(null);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Permanently delete this file? This cannot be undone.')) {
      return;
    }

    try {
      setDeletingFileId(fileId);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
      const response = await fetch(`${API_URL}/admin/candidates/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to delete file');

      refetch();
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file: ' + err.message);
    } finally {
      setDeletingFileId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Error loading candidate: {error || 'Not found'}</p>
        <Button onClick={() => navigate('/recruiter/candidates')} className="mt-4">
          Back to Candidates
        </Button>
      </div>
    );
  }

  return (
    <DetailContainer>
      {/* Header */}
      <DetailContainer.Header>
        <DetailContainer.TitleSection>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
            onClick={() => navigate('/recruiter/candidates')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <DetailContainer.Title
            title={`${candidate.first_name} ${candidate.last_name}`}
            subtitle={candidate.email}
          />
        </DetailContainer.TitleSection>
        <DetailContainer.Actions>
          <Badge className={
            candidate.status === 'Active' ? 'bg-green-100 text-green-800' :
            candidate.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
            'bg-secondary text-secondary-foreground'
          }>
            {candidate.status}
          </Badge>
          <Button
            variant="outline"
            onClick={() => handleGenerateProfile(false)}
            disabled={generatingProfile !== null}
            size="sm"
            className="md:size-default"
          >
            {generatingProfile === 'full' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            <span className="hidden sm:inline">Extract Indicative Profile</span>
            <span className="sm:hidden">Profile</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => handleGenerateProfile(true)}
            disabled={generatingProfile !== null}
            size="sm"
            className="md:size-default"
          >
            {generatingProfile === 'redacted' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <EyeOff className="h-4 w-4 mr-2" />
            )}
            <span className="hidden sm:inline">De-identified</span>
            <span className="sm:hidden">De-id</span>
          </Button>
          <Button onClick={() => setEditModalOpen(true)} size="sm" className="md:size-default">
            <Edit className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Edit Candidate</span>
            <span className="sm:hidden">Edit</span>
          </Button>
        </DetailContainer.Actions>
      </DetailContainer.Header>

      {/* Basic Info */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            <div className="flex items-start gap-2">
              <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-sm sm:text-base text-foreground break-all">{candidate.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Phone</p>
                <p className="text-sm sm:text-base text-foreground break-all">{candidate.phone || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Location</p>
                <p className="text-sm sm:text-base text-foreground break-words">
                  {candidate.location_city && candidate.location_state
                    ? `${candidate.location_city}, ${candidate.location_state}, ${candidate.location_country || 'Australia'}`
                    : 'Not provided'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Security Clearance</p>
                <Badge className="mt-1 bg-blue-100 text-blue-800 text-xs">
                  {candidate.clearance_level || 'None'}
                </Badge>
              </div>
            </div>
          </div>

          {candidate.notes && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-2">Notes</p>
              <p className="text-foreground whitespace-pre-wrap">{candidate.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employment Details */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Employment Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Current Position</p>
              <p className="text-foreground mt-1">{candidate.position?.name || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Payroll Type</p>
              <p className="text-foreground mt-1">{candidate.payroll_type || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">PTY LTD ABN</p>
              <p className="text-foreground mt-1">{candidate.pty_ltd_abn || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Citizenship Status</p>
              <p className="text-foreground mt-1">{candidate.citizenship_status || 'Not specified'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Skills</CardTitle>
          <CardDescription>{skills.length} skill{skills.length !== 1 ? 's' : ''} listed</CardDescription>
        </CardHeader>
        <CardContent>
          {skills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {skills.map((candidateSkill) => (
                <Badge key={candidateSkill.id} variant="secondary">
                  {candidateSkill.skill?.name}
                  {candidateSkill.level && ` (${candidateSkill.level})`}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No skills listed</p>
          )}
        </CardContent>
      </Card>

      {/* Public Display */}
      <CandidatePublicDisplay candidate={candidate} skills={skills} />

      {/* Files */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center text-lg sm:text-xl">
            <FileText className="h-5 w-5 mr-2" />
            Files & Documents
          </CardTitle>
          <CardDescription>{files.length} file{files.length !== 1 ? 's' : ''} uploaded</CardDescription>
        </CardHeader>
        <CardContent>
          {files.length > 0 ? (
            <div className="space-y-3">
              {files.map((file) => (
                <div key={file.id} className={`flex items-center justify-between p-3 rounded-lg ${file.status === 'pending_deletion' ? 'bg-red-50 border border-red-200' : 'bg-accent'}`}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-foreground">{file.file_name}</p>
                      {file.status === 'pending_deletion' && (
                        <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300">
                          For Deletion
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {FILE_TYPE_LABELS[file.file_type] || file.file_type} • {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(file.file_url, '_blank')}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteFile(file.id)}
                      disabled={deletingFileId === file.id}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      {deletingFileId === file.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No files uploaded</p>
          )}
        </CardContent>
      </Card>

      {/* Social Links */}
      <SocialLinksManager candidateId={id} isAdmin={true} />

      {/* Applications */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center text-lg sm:text-xl">
            <Briefcase className="h-5 w-5 mr-2" />
            Applications
          </CardTitle>
          <CardDescription>{applications.length} RFQ application{applications.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {applications.length > 0 ? (
            <div className="space-y-3">
              {applications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-4 bg-accent rounded-lg cursor-pointer hover:bg-accent/80"
                  onClick={() => navigate(`/recruiter/applications/${app.id}`)}
                >
                  <div>
                    <p className="font-medium text-foreground">{app.rfq?.ref_code}</p>
                    <p className="text-sm text-muted-foreground">{app.rfq?.title}</p>
                    {app.rfq?.client && (
                      <p className="text-xs text-muted-foreground mt-1">Client: {app.rfq.client.name}</p>
                    )}
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">{app.status}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No applications yet</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Candidate Modal */}
      <AddCandidateModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        editMode={true}
        existingCandidate={candidate}
        onSuccess={() => {
          refetch();
          setEditModalOpen(false);
        }}
      />
    </DetailContainer>
  );
};

export default CandidateDetail;
